import { useQuery } from "@tanstack/react-query";
import { menuVisibilityQuery, projectFeaturesQuery, projectMenuVisibilityQuery } from "@/lib/shellQueries";
import { ADMIN_MENU_ITEMS, COMPANY_MENU_ITEMS, PROJECT_MENU_ITEMS, foldTabGroups, tabGroupOf, type NavMenuItem, type TabGroupId } from "@shared/navigationMenu";
import { useUIContext } from "@/contexts/UIContext";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";
import { canAccessPlatformSettings } from "@/lib/platformAccess";
import { isProjectMenuOpen, type MenuGateInputs } from "@shared/menuGate";
import { chipToShow, useSeenMenuChips } from "@/lib/menuChipSeen";

export interface ProjectNavItem {
  key: string;
  title: string;
  icon: string;
  /** Absolute path, ready for a Link. */
  href: string;
  /** Platform Settings → General's chip for it: `new`, `updated`, `coming_soon`. */
  chip?: string | null;
  /** A folded tab group (Sales): the open tabs, in order. The row is active on any of them. */
  tabs?: ProjectNavItem[];
}

/** Whether `location` is on this row's page (or, for a group, any of its tabs' pages). */
export function navItemMatches(item: ProjectNavItem, location: string): boolean {
  const hrefs = item.tabs?.length ? item.tabs.map((t) => t.href) : [item.href];
  return hrefs.some((h) => location === h || location.startsWith(h + "/"));
}

/**
 * The project's navigation as the reader is allowed to see it — the same
 * filter `ModeAwareSidebar` applies, so the phone tab bar and the sidebar can
 * never disagree about what a project offers.
 *
 * Four gates, in the sidebar's order: the platform-wide visibility toggle
 * (Platform Settings → Features), the per-project toggle, the user's
 * role-based menu permissions, and — for Email Marketing — an active
 * subscription. A SUPER_ADMIN always sees Project Settings so they cannot
 * lock themselves out.
 *
 * Reads the same query keys the sidebar does, so React Query serves both from
 * one fetch and the two stay in step when a toggle changes.
 */
/**
 * The SaleFish Admin area's navigation, for the tab bar: every admin
 * destination the Features toggles leave visible — the single gate
 * `ModeAwareSidebar` applies in global mode — in the sidebar's order.
 * Non-admins never enter this context and get nothing.
 */
export function useGlobalNav(): ProjectNavItem[] {
  const { user } = useAuth();
  const t = useTranslation();
  const seen = useSeenMenuChips();
  const { data: menuVisibilityData } = useQuery<any[]>(menuVisibilityQuery());
  const isAdmin = canAccessPlatformSettings(user);
  if (!isAdmin) return [];
  const isSuper = user?.role === "SUPER_ADMIN";
  return ADMIN_MENU_ITEMS.filter((item: NavMenuItem) => {
    if (item.superAdminOnly && !isSuper) return false;
    const setting = menuVisibilityData?.find((s) => s.menuKey === item.key);
    return !setting || setting.visible === true;
  }).map((item) => ({
    key: item.key,
    title: item.labelKey ? t(item.labelKey, item.label) : item.label,
    icon: item.icon,
    href: item.url,
    chip: chipToShow(item.key, menuVisibilityData?.find((s) => s.menuKey === item.key), seen),
  }));
}

/** The company-admin area's navigation, same gate. */
export function useCompanyNav(): ProjectNavItem[] {
  const t = useTranslation();
  const seen = useSeenMenuChips();
  const { data: menuVisibilityData } = useQuery<any[]>(menuVisibilityQuery());
  return COMPANY_MENU_ITEMS.filter((item: NavMenuItem) => {
    const setting = menuVisibilityData?.find((s) => s.menuKey === item.key);
    return !setting || setting.visible === true;
  }).map((item) => ({
    key: item.key,
    title: item.labelKey ? t(item.labelKey, item.label) : item.label,
    icon: item.icon,
    href: item.url,
    chip: chipToShow(item.key, menuVisibilityData?.find((s) => s.menuKey === item.key), seen),
  }));
}

/** The project's rows, tab groups folded into one row (the sidebar's and tab bar's view). */
export function useProjectNav(): ProjectNavItem[] {
  const t = useTranslation();
  const items = useProjectNavItems();
  return foldTabGroups(items, tabGroupOf, (group, tabs) => ({
    key: group.key,
    title: t(group.labelKey, group.label),
    icon: group.icon,
    href: tabs[0].href,
    tabs,
  }));
}

/** The open tabs of one group, in order — what the group's pages show as their tab strip. */
export function useTabGroup(group: TabGroupId): ProjectNavItem[] {
  return useProjectNavItems().filter((i) => tabGroupOf(i.key) === group);
}

/** Every project menu item the reader may open, unfolded. */
function useProjectNavItems(): ProjectNavItem[] {
  const { mode, projectId } = useUIContext();
  const { user } = useAuth();
  const t = useTranslation();

  const { data: menuVisibilityData } = useQuery<any[]>(menuVisibilityQuery());
  // The sidebar's own definitions (lib/shellQueries.ts): one key, one fetcher,
  // one freshness, so a page that mounts a tab strip refetches nothing.
  const { data: projectMenuVisibilityData } = useQuery<Record<string, boolean> | null>(
    projectMenuVisibilityQuery(projectId, mode === "project"),
  );
  const { data: projectFeaturesData } = useQuery(projectFeaturesQuery(projectId));

  if (mode !== "project" || !projectId) return [];

  // The project's company holds it (a company plan line since 2026-10-20).
  const hasEmailMarketing = projectFeaturesData?.emailMarketing === true;
  const gate: MenuGateInputs = {
    globalVisibility: menuVisibilityData,
    projectVisibility: projectMenuVisibilityData ?? {},
    rolePermissions: (user as any)?.menuPermissions,
    role: user?.role,
    hasEmailMarketing,
    hasShowroom: projectFeaturesData?.showroom === true,
    hasAgentPortal: projectFeaturesData?.agentPortal === true,
    isCompanyAdmin: user?.isCompanyAdmin === true,
    isSigningOfficer: projectFeaturesData?.signingOfficer === true,
  };

  return PROJECT_MENU_ITEMS.filter((item: NavMenuItem) => {
    return isProjectMenuOpen(gate, item.key);
  }).map((item) => ({
    key: item.key,
    title: item.labelKey ? t(item.labelKey, item.label) : item.label,
    icon: item.icon,
    href: item.url.startsWith("/") ? item.url : `/projects/${projectId}/${item.url}`,
  }));
}
